import React, { useState, useEffect } from "react";
import { ArrowRight, Clock, CheckCircle } from "lucide-react";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
} from "../extended-warranty/ExtendedWarrantyScreenWrapper";
import { buildFlowUrl } from "../../utils/FlowUrlBuilder";
import analyticsService from "../../lib/AnalyticsService";

const RefinancingWelcomeScreen = ({ onComplete }) => {
  const [showOwnliModal, setShowOwnliModal] = useState(false);

  useEffect(() => {
    analyticsService.trackEvent("Refinancing Page View", {
      page: "refi_welcome",
      screen: "welcome",
      flow_type: "refinancing",
    });
  }, []);

  const steps = [
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Review your verified profile",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Confirm a few quick details",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Check potential savings",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Explore options from trusted lenders",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Earn from your Ownli refinance profile",
    },
  ];

  return (
    <ExtendedWarrantyScreenWrapper>
      <ExtendedWarrantyHeader
        title="See if refinancing can lower your payment"
        subtitle="You're in a great window to compare refinancing options. Since your profile is already verified with Ownli, this will be quick."
      >
        <img
          src="/refinancing/Why-Refinance.png"
          alt="Why refinance your auto loan"
          className="w-full mx-auto mt-5 object-contain rounded-lg"
        />

      </ExtendedWarrantyHeader>

      <ExtendedWarrantyCard variant="info">
        <div className="flex items-center gap-3">
          <Clock className="min-w-[25px] min-h-[25px] text-green" />
          <p className="text-base text-darkGrey">
            Takes less than 3 minutes to check your refinance options
          </p>
        </div>
      </ExtendedWarrantyCard>

      <ExtendedWarrantyCard>
        <div>
          <h2 className="text-base font-bold mb-4">
            Refinancing process:
          </h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="mt-2 min-w-[10px] min-h-[10px] bg-darkGreen rounded-full"></span>
                <div>
                  <h3 className="font-base text-darkGrey">{step.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ExtendedWarrantyCard>

      {/* Why Ownli */}
      <ExtendedWarrantyCard variant="info">
        <p className="text-sm text-darkGrey">
          Why Waters partnered with Ownli?{" "}
          <button
            onClick={() => setShowOwnliModal(true)}
            className="text-indigo-600 hover:text-indigo-700 underline font-semibold"
          >
            Learn More
          </button>
        </p>
      </ExtendedWarrantyCard>

      {/* Ownli Modal */}
      {showOwnliModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-3">Why you can trust Ownli:</h3>
            <p className="text-gray-700 text-sm mb-4">
              Waters Car Wash partnered with Ownli to bring you more value beyond the wash. We connect you to trusted auto partners, exclusive offers, and optional ways to save or earn, all built around transparency and control.
            </p>
            <button
              onClick={() => setShowOwnliModal(false)}
              className="w-full bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <button
          onClick={() => {
            analyticsService.trackEvent("Refinancing Button Click", {
              button_name: "start-now",
              screen: "welcome",
            });
            onComplete();
          }}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          START NOW
          <ArrowRight className="w-4 h-4" />
        </button>

        <p className="text-lg text-gray-600 text-center mb-2">
          Check out other ways to save:
        </p>
        <button
          onClick={() => {
            analyticsService.trackEvent("Refinancing Flow Exit", {
              exit_point: "welcome",
              destination: "insurance_flow",
              flow_type: "refinancing",
            });
            window.location.href = buildFlowUrl("insurance");
          }}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-white text-ownli border-2 border-ownli px-4 py-3 rounded-2xl hover:bg-purple-50 transition-colors"
        >
          Insurance Offers
        </button>
        <div className="text-center">
          <a
            href="https://www.ownli.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center text-sm"
            onClick={() => {
              analyticsService.trackEvent("Refinancing Flow Exit", {
                exit_point: "welcome",
                destination: "ownli_app",
                flow_type: "refinancing",
              });
            }}
          >
            Other offers at Ownli
          </a>
        </div>

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </ExtendedWarrantyScreenWrapper>
  );
};

export default RefinancingWelcomeScreen;
